import { TechnicalProductOP, ValueAddedOP } from '@cemex-core/cmx-api';
import {
  OrderBuilderActions,
  RESET_BUILDER,
} from '@ordersnproducts/modules/order-builder/actions/order-builder.actions';
import {
  AddedValueActionTypes,
  AddedValuesActions,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/open-platform/added-values.actions';

export interface AddedValueTuple {
  loading: boolean;
  addedValues: ValueAddedOP[];
  technicalProduct: TechnicalProductOP[];
}

export interface AddedValuesState {
  availableAddedValues: {
    [productCode: string]: AddedValueTuple[];
  };
  valueAddedsWithoutDco: {
    [index: number]: {
      specialized: boolean;
      nothasNotAlloedCombinations: boolean;
    };
  };
  temperatureValues: {
    [formId: string]: ValueAddedOP[];
  };
}

export const initialState: AddedValuesState = {
  availableAddedValues: {},
  valueAddedsWithoutDco: {},
  temperatureValues: {},
};

/**
 * Makes tuple state into loading state given index
 * Will remove further tuples from index
 * @param state
 * @param index
 */
const loadTupleState = (state: AddedValueTuple[], index: number) => {
  if (index === 0) {
    state = [];
  } else {
    state.splice(index + 1, state.length - (index + 1));
  }

  state[index] = {
    addedValues: undefined,
    technicalProduct: [],
    loading: true,
  };

  return state;
};

/**
 * Makes a tuple state into a complete mode
 * Adds available added values options into given index
 * @param state
 * @param addedValues
 * @param index
 */
const completeTupleState = (
  state: AddedValueTuple[],
  addedValues: ValueAddedOP[],
  technicalProduct: TechnicalProductOP[],
  index: number,
) => {
  return (
    !!state &&
    state.map((tuple, i) => {
      if (i === index) {
        return {
          addedValues: addedValues,
          technicalProduct: technicalProduct,
          loading: false,
        } as AddedValueTuple;
      } else {
        return tuple;
      }
    })
  );
};

export function addedValuesReducer(
  state = initialState,
  action: AddedValuesActions | OrderBuilderActions,
): AddedValuesState {
  switch (action.type) {
    case AddedValueActionTypes.CLEAN_NULL_ADDED_VALUES_OP: {
      const keys = Object.keys(state.availableAddedValues).map((k) => Number(k));
      const _state = { ...state };
      keys.forEach((productCode) => {
        const clean = state.availableAddedValues[productCode].filter(
          ({ addedValues, technicalProduct }) => addedValues !== null && technicalProduct !== null,
        );
        _state.availableAddedValues[productCode] = clean;
      });
      return {
        ...state,
        availableAddedValues: {
          ..._state.availableAddedValues,
        },
      };
    }
    case AddedValueActionTypes.LOAD_ADDED_VALUES_RMX_UL_OP:
    case AddedValueActionTypes.LOAD_ADDED_VALUES_NOT_FORMULATING_OP:
    case AddedValueActionTypes.LOAD_ADDED_VALUES_OP: {
      const keyName = `${action.payload.contractCode}_${action.payload.formId}`;
      return {
        ...state,
        availableAddedValues: {
          ...state.availableAddedValues,
          [keyName]: loadTupleState(state.availableAddedValues[keyName], action.payload.index),
        },
      } as AddedValuesState;
    }
    case AddedValueActionTypes.LOAD_AVAILABLE_ADDED_VALUES_COMPLETED_RMX_UL_OP:
    case AddedValueActionTypes.LOAD_AVAILABLE_ADDED_VALUES_COMPLETED_OP: {
      const keyName = `${action.payload.contractCode}_${action.payload.formId}`;
      return {
        ...state,
        availableAddedValues: {
          ...state.availableAddedValues,
          [keyName]: completeTupleState(
            state.availableAddedValues[keyName],
            action.payload.addedValues,
            action.payload.technicalProduct,
            action.payload.index,
          ),
        },
      } as AddedValuesState;
    }
    case AddedValueActionTypes.LOAD_TEMPERATURE_VALUES_COMPLETED: {
      return {
        ...state,
        temperatureValues: {
          ...state.temperatureValues,
          [action.payload.formId]: action.payload.addedValues,
        },
      } as AddedValuesState;
    }
    case AddedValueActionTypes.ADDED_VALUES_SPECIALIZED_OP: {
      return {
        ...state,
        valueAddedsWithoutDco: {
          ...state.valueAddedsWithoutDco,
          [action.payload.formdId]: action.payload,
        },
      } as AddedValuesState;
    }
    case RESET_BUILDER:
    case AddedValueActionTypes.RESET_OP: {
      return {
        ...initialState,
      };
    }
    case AddedValueActionTypes.RESET_OP_FORM_ID: {
      let keyName = '';
      for (const key in state.availableAddedValues) {
        if (!!key && key.endsWith(`_${action.payload.formId}`)) {
          keyName = key;
          break;
        }
      }
      const updatedAvailableAddedValues = { ...state.availableAddedValues };
      if (keyName) {
        updatedAvailableAddedValues[keyName] = [];
      }
      return {
        ...state,
        temperatureValues: {
          ...state.temperatureValues,
          [action.payload.formId]: [],
        },
        availableAddedValues: updatedAvailableAddedValues,
        valueAddedsWithoutDco: {
          ...state.valueAddedsWithoutDco,
          [action.payload.formId]: undefined,
        },
      };
    }
    default: {
      return state;
    }
  }
}
